import React, { useMemo, useState } from 'react';
import { SearchIcon, ExternalLinkIcon } from 'lucide-react';
import { Project } from '../types';
import { ageBands, difficulties, messLevels, projects, timeBands } from '../data/projects';
import { booksByNumber } from '../data/books';
import { toView } from '../data/links';
import { SectionHeading } from './SectionHeading';
type FacetKey = 'age' | 'timeBand' | 'difficulty' | 'mess';
const facets: {
  key: FacetKey;
  label: string;
  options: readonly string[];
}[] = [{
  key: 'age',
  label: 'Age',
  options: ageBands
}, {
  key: 'timeBand',
  label: 'Build time',
  options: timeBands
}, {
  key: 'difficulty',
  label: 'Difficulty',
  options: difficulties
}, {
  key: 'mess',
  label: 'Mess level',
  options: messLevels
}];
const PAGE_SIZE = 30;
const pad = (value: number) => String(value).padStart(2, '0');
export function ProjectSearch() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Record<FacetKey, string[]>>({
    age: [],
    timeBand: [],
    difficulty: [],
    mess: []
  });
  const [visible, setVisible] = useState(PAGE_SIZE);
  const activeCount = Object.values(selected).reduce((total, values) => total + values.length, 0);
  const isFiltered = activeCount > 0 || query.trim().length > 0;
  const toggle = (key: FacetKey, option: string) => {
    setVisible(PAGE_SIZE);
    setSelected(prev => {
      const values = prev[key];
      return {
        ...prev,
        [key]: values.includes(option) ? values.filter(value => value !== option) : [...values, option]
      };
    });
  };
  const clearAll = () => {
    setQuery('');
    setSelected({
      age: [],
      timeBand: [],
      difficulty: [],
      mess: []
    });
    setVisible(PAGE_SIZE);
  };
  const results = useMemo<Project[]>(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return projects.filter(project => {
      if (terms.length) {
        const book = booksByNumber[project.bookNumber];
        const haystack = `${project.title} ${project.subtitle} ${book ? book.title : ''} ${project.tags.join(' ')}`.toLowerCase();
        if (!terms.every(term => haystack.includes(term))) return false;
      }
      if (selected.age.length && !selected.age.includes(project.age)) return false;
      if (selected.timeBand.length && !selected.timeBand.includes(project.timeBand)) return false;
      if (selected.difficulty.length && !selected.difficulty.includes(project.difficulty)) return false;
      if (selected.mess.length && !selected.mess.includes(project.mess)) return false;
      return true;
    });
  }, [query, selected]);
  const shown = results.slice(0, visible);
  return <section aria-labelledby="search-heading" id="search" className="scroll-mt-24" data-id="element-169" data-mp="qwwd8q0z1ygi">
      <SectionHeading index="03" title="Find the right project" note="Every project across the twelve books, indexed by what it is made of and what it teaches. Search a material, a machine, or just what your child wants to build." data-id="element-170" data-mp="uxgcq090pe5t">
        <p className="tnum font-sans text-[12px] text-ink/45" data-id="element-171" data-mp="dmul874im3nx">{projects.length} projects indexed</p>
      </SectionHeading>

      <div className="mt-8 bg-surface p-6 sm:p-8" data-id="element-172" data-mp="9msj5wuvb884">
        <label htmlFor="project-query" className="smallcaps block font-sans text-[10px] text-ink/55" data-id="element-173" data-mp="nd5n7o4n22s6">
          Search
        </label>
        <div className="mt-2 flex items-center gap-3 border-b border-rule focus-within:border-ink" data-id="element-174" data-mp="im72mccmaij4">
          <SearchIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-ink/40" strokeWidth={1.5} data-id="element-175" data-mp="xak1bh33afhb" />
          <input id="project-query" type="search" value={query} placeholder="bridge, glider, magnet, straws, gears…" onChange={event => {
          setQuery(event.target.value);
          setVisible(PAGE_SIZE);
        }} className="w-full bg-transparent py-2.5 font-sans text-[17px] text-ink placeholder:text-ink/35 focus:outline-none" data-id="element-176" data-mp="sb76oa5z8446" />
        </div>

        <div className="mt-7 grid gap-6 sm:grid-cols-2" data-id="element-177" data-mp="336o5gyyu5pfl">
          {facets.map(facet => <fieldset key={facet.key} data-id="element-178" data-mp="35l1cjwna1qcm">
              <legend className="smallcaps font-sans text-[10px] text-ink/45" data-id="element-179" data-mp="39k0oi17fendk">{facet.label}</legend>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-180" data-mp="3ej4w4nepcd1s">
                {facet.options.map(option => {
              const isOn = selected[facet.key].includes(option);
              return <button key={option} type="button" aria-pressed={isOn} onClick={() => toggle(facet.key, option)} className={`border px-3 py-1.5 font-sans text-[12px] transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${isOn ? 'border-ink bg-ink text-onaccent' : 'border-rule text-ink/70 hover:border-ink/45 hover:text-ink'}`} data-id="element-181" data-mp="2lvr0csmqpnj9">
                      {option}
                    </button>;
            })}
              </div>
            </fieldset>)}
        </div>

        <div className="mt-8 flex flex-wrap items-baseline justify-between gap-3 border-t border-rule pt-4" data-id="element-182" data-mp="1qnyqv75c8id7">
          <p aria-live="polite" className="tnum font-sans text-[12px] text-ink/60" data-id="element-183" data-mp="1lp5bf96hqhae">
            {results.length} {results.length === 1 ? 'project' : 'projects'}
            {isFiltered ? ' · filtered' : ''}
          </p>
          {isFiltered ? <button type="button" onClick={clearAll} className="font-sans text-[12px] text-ink/60 underline decoration-rule underline-offset-4 transition-colors duration-150 ease-out hover:text-ink hover:decoration-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink" data-id="element-184" data-mp="17wfon2j518c8">
              Clear everything
            </button> : null}
        </div>

        {results.length === 0 ? <p className="mt-8 max-w-measure font-sans text-[16px] leading-relaxed text-ink/60" data-id="element-185" data-mp="o9ck7ril01c1">
            Nothing matches that combination. Try one filter fewer, or search a material instead of
            a machine: <span className="text-ink" data-id="element-186" data-mp="f8ecay50v9o6">straws</span>,{' '}
            <span className="text-ink" data-id="element-187" data-mp="fb0jgwo03n5v">foil</span>, <span className="text-ink" data-id="element-188" data-mp="12xywtlgtqezk">string</span>.
          </p> : <>
            <ul className="mt-6 divide-y divide-rule/70 border-t border-rule" data-id="element-189" data-mp="16zu89isgy8r2">
              {shown.map(project => {
            const book = booksByNumber[project.bookNumber];
            const href = book ? toView(book.file) : '';
            return <li key={project.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 py-3" data-id="element-190" data-mp="y5o0eslalbbr">
                    <span aria-hidden="true" className="mt-[7px] h-2 w-2 shrink-0 self-start" style={{
                backgroundColor: book ? book.accent : '#7B8EA3'
              }} data-id="element-191" data-mp="1lvi9cz2hqfqq" />
                    <div className="min-w-[12rem] flex-1" data-id="element-192" data-mp="1w230wwk4205d">
                      <p className="font-display text-[17px] leading-snug text-ink" data-id="element-193" data-mp="1vzgs6tlhhelk">{project.title}</p>
                      <p className="font-sans text-[14px] leading-snug text-ink/50" data-id="element-194" data-mp="24tlgp6d5adxx">
                        {project.subtitle}
                      </p>
                    </div>
                    <p className="tnum font-sans text-[12px] text-ink/55" data-id="element-195" data-mp="1h63jgvb42nnk">
                      {project.age} · {project.time} · {project.mess === 'None' ? 'no mess' : `${project.mess.toLowerCase()} mess`}
                    </p>
                    <p className="tnum font-sans text-[12px] text-ink/45" data-id="element-196" data-mp="1m3di91ke4bo2">
                      Book {pad(project.bookNumber)} · Project {pad(project.projectNumber)} · p.{project.page}
                    </p>
                    {href ? <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-sans text-[12px] text-ink/70 underline decoration-rule underline-offset-4 transition-colors duration-150 ease-out hover:text-ink hover:decoration-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink" data-id="element-197" data-mp="2xhhb24f8rtrj">
                        <ExternalLinkIcon aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-198" data-mp="1ejupi3lch42a" />
                        Open book
                      </a> : null}
                  </li>;
          })}
            </ul>
            {results.length > visible ? <button type="button" onClick={() => setVisible(prev => prev + PAGE_SIZE)} className="mt-6 border border-rule px-4 py-2.5 font-sans text-[13px] text-ink transition-colors duration-150 ease-out hover:border-ink/45 hover:bg-ink/[0.05] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-surface" data-id="element-199" data-mp="1sjvsppp30nkx">
                Show {Math.min(PAGE_SIZE, results.length - visible)} more
              </button> : null}
          </>}
      </div>
    </section>;
}